import { useMemo } from 'react';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderRow,
  DataTableHeaderActions,
  DataTableHeaderActionsLeftSection,
  DataTableHeaderActionsRightSection,
  DataTableHeaderSortMenu,
  DataTableTable,
  Panel,
  PanelInset,
  PanelSection,
  PanelFooter,
  DatePicker,
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  Paragraph,
  Text,
  Tooltip,
  Stack,
} from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getLocalTimeZone, today } from '@internationalized/date';
import type { DeliverabilityMetricRow, DeliverabilityDateRange } from '../types';
import { MetricCell } from './metric-cell';
import { DeliverabilityRatePopover } from './deliverability-rate-popover';
import { FIRST_POSSIBLE_DATE, NO_SPAM_PROVIDERS } from '../utils/constants';
import { getQualityIndicator, getQualityColor } from '../utils/quality-indicators';
import { formatPercent, formatNumber } from '@/utils/number-format';

const columnHelper = createColumnHelper<DeliverabilityMetricRow>();

interface DeliverabilityTableProps {
  data: DeliverabilityMetricRow[];
  dateRange: DeliverabilityDateRange;
  onDateRangeChange: (range: DeliverabilityDateRange) => void;
  isLoading: boolean;
  isError: boolean;
  searchValue: string;
  onSearch: (value: string) => void;
  sortingValue?: { id: string; desc: boolean };
  onSortingChange: (sorting: { id: string; desc: boolean }) => void;
  hasNoData: boolean;
}

export function DeliverabilityTable({
  data,
  dateRange,
  onDateRangeChange,
  isLoading,
  isError,
  searchValue,
  onSearch,
  sortingValue,
  onSortingChange,
  hasNoData,
}: DeliverabilityTableProps) {
  const todayDate = today(getLocalTimeZone());

  const handleDatePickerChange = (value: unknown) => {
    if (value != null && typeof value === 'object' && 'start' in value && 'end' in value) {
      onDateRangeChange(value as DeliverabilityDateRange);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('provider', {
        header: 'Provider',
        cell: (info) => (
          <Text text="product-p" weight="medium" truncate>
            {info.getValue()}
          </Text>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('sentPercentage', {
        header: 'Outbound',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => {
          const formattedPercent = formatPercent(info.getValue(), 1);
          const formattedCount = formatNumber(info.row.original.sentCount);

          return (
            <Stack>
              <Text>{formattedPercent}</Text>
              <Text color="subtle" size="xs">
                {formattedCount}
              </Text>
            </Stack>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor('deliveryRate', {
        header: 'Delivered',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => (
          <MetricCell percent={info.getValue()} count={info.row.original.deliveryCount} metricName="deliveryRate" />
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('bounceRate', {
        header: 'Bounced',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => (
          <MetricCell
            percent={info.getValue()}
            count={info.row.original.bounceCount}
            metricName="bounceRate"
            isInverse
          />
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('openRate', {
        header: 'Opened',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => {
          const quality = getQualityIndicator('openRate', info.getValue());
          const color = getQualityColor(quality);
          const formattedPercent = formatPercent(info.getValue(), 1);
          const formattedCount = formatNumber(info.row.original.openCount);

          return (
            <DeliverabilityRatePopover
              isClick={false}
              row={info.row.original}
              formattedPercent={formattedPercent}
              formattedCount={formattedCount}
              color={color}
            />
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor('clickRate', {
        header: 'Clicked',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => {
          const quality = getQualityIndicator('clickRate', info.getValue());
          const color = getQualityColor(quality);
          const formattedPercent = formatPercent(info.getValue(), 1);
          const formattedCount = formatNumber(info.row.original.clickCount);

          return (
            <DeliverabilityRatePopover
              isClick={true}
              row={info.row.original}
              formattedPercent={formattedPercent}
              formattedCount={formattedCount}
              color={color}
            />
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor('spamRate', {
        header: 'Spammed',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => {
          const { provider } = info.row.original;
          if (NO_SPAM_PROVIDERS.includes(provider as (typeof NO_SPAM_PROVIDERS)[number])) {
            return (
              <Tooltip placement="right" content={`${provider} does not report spam`}>
                <span>—</span>
              </Tooltip>
            );
          }
          return (
            <MetricCell percent={info.getValue()} count={info.row.original.spamCount} metricName="spamRate" isInverse />
          );
        },
        enableSorting: true,
      }),
    ],
    [],
  );

  return (
    <DataTable
      caption="Inbox Providers"
      data={data}
      columns={columns}
      withSorting={true}
      sorting={{
        value: sortingValue,
        onChange: onSortingChange,
        isManual: false,
      }}
      withSearch={true}
      search={{
        value: searchValue,
        onSearchInput: onSearch,
        onSearch: onSearch,
        shouldSearchOnInput: true,
        shouldSearchOnClear: true,
        isManual: false,
      }}
      isLoading={isLoading}
    >
      <Panel>
        <DataTableHeader>
          <DataTableHeaderRow>
            <DataTableHeaderActions>
              <DataTableHeaderActionsLeftSection />
              <DataTableHeaderActionsRightSection>
                <DatePicker
                  ariaLabel="Date range"
                  value={dateRange}
                  onChange={handleDatePickerChange}
                  isRange
                  withPresets
                  minDate={FIRST_POSSIBLE_DATE}
                  maxDate={todayDate}
                  size="sm"
                />
                <DataTableHeaderSortMenu />
              </DataTableHeaderActionsRightSection>
            </DataTableHeaderActions>
          </DataTableHeaderRow>
        </DataTableHeader>

        {isError ? (
          <PanelSection>
            <EmptyState>
              <EmptyStateTitle icon="error">Error loading data</EmptyStateTitle>
              <EmptyStateDescription>
                We encountered an error while loading the data. Please try again later.
              </EmptyStateDescription>
            </EmptyState>
          </PanelSection>
        ) : hasNoData && !isLoading ? (
          <PanelSection>
            <EmptyState>
              <EmptyStateTitle icon="analysis">No data available</EmptyStateTitle>
              <EmptyStateDescription>
                No deliverability data available for the selected date range.
              </EmptyStateDescription>
            </EmptyState>
          </PanelSection>
        ) : (
          <PanelInset>
            <DataTableTable />
          </PanelInset>
        )}

        <PanelFooter withBorder>
          <Paragraph variant="secondary">Deliverability information is available starting April 15th, 2025</Paragraph>
        </PanelFooter>
      </Panel>
    </DataTable>
  );
}
